import { Alert, AlertDescription, AlertIcon, Center, Spinner, VStack } from '@chakra-ui/react';
import { SampleSheetViewForOrganisation } from '@resistapp/client/views/sample-sheet-view/sample-sheet-view';
import { LabFeature } from '@resistapp/common/features';
import { useEffect, useMemo, useRef } from 'react';

import type { LabWorkbookSummary } from '../api/use-lab-workbook';
import { LabPageHeader } from '../components/lab-page-header';
import { LegacyLabSheetImportControl } from '../components/legacy-lab-sheet-import-control';
import { useLabLayoutContext } from '../lab-layout';

export function SampleAssayPage() {
  const { workbookQuery, viewerAccess, workbookId, currentStepId } = useLabLayoutContext();
  const { data, error, isLoading, isError } = workbookQuery;
  const cachedWorkbookRef = useRef<LabWorkbookSummary | null>(null);
  useEffect(() => {
    if (data) {
      cachedWorkbookRef.current = data;
    }
  }, [data]);

  const workbook = useMemo(() => data ?? cachedWorkbookRef.current, [data]);
  const blockingError = isError && !workbook;
  const offlineError = isError && Boolean(workbook);
  const errorMessage =
    error instanceof Error ? error.message : 'Failed to load workbook. Check your internet connection and try again.';

  const labFeatureSet = useMemo(() => new Set(viewerAccess.labFeatures), [viewerAccess.labFeatures]);
  const organisationId = workbook?.sampleSheet.organisationId;
  const sampleSheetId = workbook?.sampleSheet.id ?? null;
  const labSheetUrl = workbook ? workbook.sampleSheet.labSheetUrl : null;
  const channelUrl = workbook ? workbook.workbook.channelUrl : null;
  const rawWorkbookName = workbook?.workbook.name;
  const workbookTitle = rawWorkbookName ? rawWorkbookName.trim() : '';
  const baseHeaderTitle = currentStepId === 'gene-template' ? 'Assay Template' : 'Sample Specification';
  const headerTitle = workbookTitle ? `${workbookTitle} — ${baseHeaderTitle}` : baseHeaderTitle;
  const labWorkbookStatus = workbook?.workbook.status;

  const lacksGeneAccess =
    currentStepId === 'gene-template' && !viewerAccess.hasFullAccess && !labFeatureSet.has(LabFeature.GENE_TEMPLATE);
  const lacksSampleSpecAccess =
    currentStepId === 'sample-spec' && !viewerAccess.hasFullAccess && !labFeatureSet.has(LabFeature.SAMPLE_SPEC);

  if (isLoading && !workbook) {
    return (
      <Center minH="320px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (!viewerAccess.canViewSpec || lacksGeneAccess || lacksSampleSpecAccess) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription>
          You do not have access to the SmartChip specification for this lab workbook.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {blockingError ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}
      {offlineError ? (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Connection lost. Showing the last loaded workbook so you can keep working. Changes will sync once the server
            is reachable again.
          </AlertDescription>
        </Alert>
      ) : null}
      {organisationId != null ? (
        <>
          {(() => {
            const shouldShowLegacyImport =
              !labSheetUrl &&
              viewerAccess.hasFullAccess &&
              workbook?.legacyImport.eligible &&
              (currentStepId === 'gene-template' || currentStepId === 'sample-spec');
            const activeLegacyOrganisationId =
              shouldShowLegacyImport && typeof organisationId === 'number' ? organisationId : null;
            const headerControl =
              activeLegacyOrganisationId != null ? (
                <LegacyLabSheetImportControl
                  workbookId={workbookId}
                  organisationId={activeLegacyOrganisationId}
                />
              ) : undefined;

            return (
              <LabPageHeader title={headerTitle} labSheetUrl={labSheetUrl} channelUrl={channelUrl} control={headerControl} />
            );
          })()}
          <SampleSheetViewForOrganisation
            organisationId={organisationId}
            sampleSheetId={sampleSheetId}
            labWorkbookStatus={labWorkbookStatus}
            labWorkbookId={workbookId}
            allowGeneTemplateEdits={viewerAccess.hasFullAccess || !viewerAccess.isAssignedLabMember}
          />
        </>
      ) : null}
    </VStack>
  );
}
